/**
 * Project selector component with modern styling
 */

import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Select } from '../UI/Select';
import { listProjects } from '../../api/projects';
import { useWorkspace } from '../../hooks/useWorkspace';
import type { Project } from '../../types/api';
import { FolderKanban } from 'lucide-react';

interface ProjectSelectorProps {
  defaultProjectId?: string;
}

export function ProjectSelector({ defaultProjectId }: ProjectSelectorProps = {}) {
  const { tenantId, project, projects, setProject, setProjects } = useWorkspace();
  
  const { data, isPending, error, isError } = useQuery<Project[]>({
    queryKey: ['projects', tenantId],
    queryFn: async () => {
      console.log('[ProjectSelector] Fetching projects for tenantId:', tenantId);
      try {
        const result = await listProjects(tenantId || undefined);
        console.log('[ProjectSelector] Projects fetched successfully:', result?.length || 0, 'projects');
        return result;
      } catch (err) {
        console.error('[ProjectSelector] Error fetching projects:', err);
        throw err;
      }
    },
    enabled: !!tenantId,
    retry: 2,
    staleTime: 30000, // Consider data fresh for 30 seconds
  });
  
  // Debug logging
  useEffect(() => {
    console.log('[ProjectSelector] State:', {
      tenantId,
      isPending,
      isError,
      hasData: !!data,
      dataLength: data?.length || 0,
      error: error?.message,
    });
  }, [tenantId, isPending, isError, data, error]);

  // Track if we've initialized from localStorage
  const [hasInitialized, setHasInitialized] = React.useState(false);

  useEffect(() => {
    if (data) {
      setProjects(data);
    }
  }, [data, setProjects]);

  // Auto-select project ONLY if no project is currently selected
  // Respects user's existing selection across page navigations
  // EXCEPTION: If project is selected but doesn't match defaultProjectId, re-select (fixes refresh issue)
  useEffect(() => {
    // Skip if already initialized (prevents re-running on every mount)
    if (hasInitialized) {
      return;
    }

    if (data && data.length > 0) {
      // If defaultProjectId is provided, try to find it
      if (defaultProjectId) {
        const defaultProject = data.find((p: Project) => p.id === defaultProjectId);
        if (defaultProject) {
          // Auto-select if:
          // 1. No project is selected
          // 2. Current project doesn't match the default (fixes refresh/state change issues)
          if (!project || project.id !== defaultProjectId) {
            if (project && project.id !== defaultProjectId) {
              console.log('[ProjectSelector] Correcting project to match default:', {
                current: project.name,
                expected: defaultProject.name
              });
            } else {
              console.log('[ProjectSelector] Auto-selecting default project:', defaultProject.name);
            }
            setProject(defaultProject);
            setHasInitialized(true);
            return;
          } else {
            // Project already selected and matches default
            console.log('[ProjectSelector] Project already selected and matches default:', project.name);
            setHasInitialized(true);
            return;
          }
        } else {
          console.warn(`[ProjectSelector] Default project ID ${defaultProjectId} not found in projects list. Available projects:`, data.map(p => ({ id: p.id, name: p.name })));
        }
      }
      
      // Fallback: If no defaultProjectId or it's not found
      if (!project) {
        console.log('[ProjectSelector] Auto-selecting first available project:', data[0].name);
        setProject(data[0]);
        setHasInitialized(true);
      } else {
        console.log('[ProjectSelector] Project already selected:', project.name, '- skipping auto-selection');
        setHasInitialized(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, defaultProjectId]); // Only depend on data and defaultProjectId

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = (data || projects).find((p: Project) => p.id === e.target.value);
    if (selected) {
      setProject(selected);
    }
  };

  if (!tenantId) {
    return null;
  }

  // Show loading state only if we don't have cached data
  if (isPending && !data && !projects.length) {
    return (
      <div className="w-64 h-10 bg-slate-100 animate-pulse rounded-lg"></div>
    );
  }

  // Show error state
  if (isError) {
    console.error('[ProjectSelector] Error loading projects:', error);
    // Still show projects if we have cached data
    if (!projects.length) {
      return (
        <div className="w-64 h-10 bg-red-50 border border-red-200 rounded-lg flex items-center justify-center text-xs text-red-600">
          Failed to load
        </div>
      );
    }
  }

  const options = [
    { value: '', label: 'Select project...', disabled: true },
    ...(data || projects).map((p: Project) => ({ value: p.id, label: p.name }))
  ];

  return (
    <div className="relative">
      <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10">
        <FolderKanban className="h-4 w-4 text-slate-400" />
      </div>
      <select
        value={project?.id || ''}
        onChange={handleChange}
        className="w-80 h-10 pl-10 pr-10 text-sm font-semibold bg-white border-2 border-slate-200 rounded-lg hover:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all appearance-none cursor-pointer text-slate-700"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value} disabled={opt.disabled}>
            {opt.label}
          </option>
        ))}
      </select>
      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
        <svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  );
}
